import React, { useEffect, type FC } from 'react';

import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { expVal } from '@confluence/feature-experiments';
import { HydrationSafeSuspense } from '@confluence/hydration-utils/entry-points/HydrationSafeSuspense.tsx';
import { LoadablePaint } from '@confluence/loadable';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

type CommentTypes = {
	pageWidthType?: string;
	showLabels: boolean;
	hasReactions: boolean;
	editorIsUserOffline?: boolean;
	hasCommentSessions?: boolean;
};

const ObjectCommentContainerLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ObjectCommentContainer" */ './ObjectCommentContainer.tsx'
			)
		).ObjectCommentContainer,
});

export const ObjectCommentWrapper: FC<CommentTypes> = ({
	pageWidthType,
	showLabels,
	hasReactions,
	editorIsUserOffline,
	hasCommentSessions,
}) => {
	const { setShowGeneralComments } = useCommentsPanelActions();

	useEffect(() => {
		setShowGeneralComments(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to set the comment type once on the comment mount
	}, []);

	const content = (
		<ObjectCommentContainerLoader
			pageWidthType={pageWidthType}
			showLabels={showLabels}
			hasReactions={hasReactions}
			editorIsUserOffline={editorIsUserOffline}
			hasCommentSessions={hasCommentSessions}
		/>
	);

	return (
		<SuspenseWithDataFetcher dataFetcherName="object-comments">
			{/* Non-streaming hydration has no outer hydration lock. Keep parent updates from
			    replacing the server comments while retaining the fallback for SPA interactions. */}
			{!StatsigConfigurations.isKillSwitchOn('confluence_vp_comment_threads_frag_reader') &&
			expVal('confluence_exp_conversation_contract', 'isEnabled', false) ? (
				<HydrationSafeSuspense fallback={<CommentPlaceholder showPlaceholder />}>
					{content}
				</HydrationSafeSuspense>
			) : (
				content
			)}
		</SuspenseWithDataFetcher>
	);
};
